import CustomPlanModal from "components/account/custom-plan-modal";
import AlertBanner from "components/alerts/banner";
import Card from "components/card/card";
import DailyStats from "components/dashboard/daily-stats";
import TopInteractions from "components/dashboard/top-interactions";
import TopPopouts from "components/dashboard/top-popouts";
import TotalStats from "components/dashboard/total-stats";
import WelcomeCard from "components/dashboard/welcome-card";
import { FilterSelector } from "components/global/filterSelector";
import Select from "components/select/select";
import { Context } from "context/context";
import { usePusherEvent } from "context/pusher";
import Layout from "layouts/layout";
import _ from "lodash";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useNotifications } from "redux/modules/notification/hooks";
import { getMonths, getWeeks } from "utils/constant";
import { useDialogHook } from "utils/customhooks";
import { sendCheckoutSuccessful } from "utils/gtm-triggers";

const filters = [
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

const Dashboard = ({ userData }) => {
  const { http } = global.services;
  const { setNotificationBanner, banner } = useNotifications();
  const [custom, setCustomPlan] = useState({ isCustom: false, data: {} });
  const { currentPlan, setCurrentPlan } = useContext(Context);
  const globalContext = useContext(Context);
  const [filterDates, setFilterDates] = useState({
    start: moment().unix(),
    end: moment().unix(),
  });
  const [dateOptions, setDateOptions] = useState([]);
  const customPlanModal = useDialogHook(CustomPlanModal); //modal
  const [init, setInit] = useState(false);

  const [filter, setFilter] = useState("weekly");
  const [filterValue, setFilterValue] = useState("");

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("sucessful_payment") === "true") {
      sendCheckoutSuccessful();
      localStorage.removeItem("trialCode");
      localStorage.removeItem("trialDays");
      // Now strip the URL parameters
      history.replace(location.pathname);
    }
  }, [location, history]);

  const fetchLatestBanner = async () => {
    try {
      const { data: response } = await http.get("notifications/banner");
      if (response.data) {
        setNotificationBanner(response.data);
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  const fetchDashboardMetrics = async () => {
    try {
      const params = {
        start_date: filterDates.start,
        end_date: filterDates.end,
      };

      const { data: response } = await http.get("dashboard/metrics", params);
      if (response.data) {
        if (!response.popupExist) {
          globalContext.setAlerts([
            {
              message:
                "We’re happy you’re finally here! Create your first popout now",
              data: null,
              button: {
                name: "Get started",
                link: "/campaigns/popup",
              },
              type: "first-timer",
            },
          ]);
        } else if (userData.user.status === "suspended") {
          globalContext.setAlerts([
            {
              message: "Important: Your account is currently suspended.",
              data: null,
              button: {
                name: "Please contact support here.",
                link: "https://support.conversioncow.com/hc/en-us",
                target: "_blank",
              },
              type: "suspended-account",
            },
          ]);
        } else if (userData.user.status === "failed_pm") {
          globalContext.setAlerts([
            {
              message:
                "Important: Your subscription is suspended as your last payment failed.",
              data: null,
              insideButton: {
                name: "Update your payment details",
                link: "/dashboard/account/detail",
                afterButtonText: " to reactivate",
              },
              type: "no-subscription",
            },
          ]);
        }
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  const checkCustomSubscriptions = async () => {
    try {
      const { data: response } = await http.get("custom-subscriptions/request");
      //update here
      if (response?.data && userData.user.email_verified_at) {
        setCustomPlan({ isCustom: true, data: response?.data });
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  const setUpdateToCustomPlan = async () => {
    try {
      const result = await http.get(`subscriptions/${userData.user.id}`);
      const data = result.data.data;

      let payload = _.clone(data);
      let trialing = data.stripe_status === "trialing";
      payload.name = trialing ? `${data.name} (Free Trial)` : data.name;
      payload.stripe_product = data.items[0].stripe_product;
      setCurrentPlan(payload);
      setCustomPlan({ isCustom: false, data: {} });
    } catch (error) {
      if (error.status === 422)
        toast.error("It appears that something went wrong");
    }
  };

  const handleShowDetails = (data) => {
    customPlanModal({ data }, (result) => {
      if (result) {
        setUpdateToCustomPlan();
      }
    });
  };

  usePusherEvent(
    "private-client-notifications",
    "banner-notification-created-event",
    (data) => setNotificationBanner(data)
  );

  const loadingPlan = () => {
    let result = false;
    if (userData?.user?.role !== "admin") {
      if (_.isEmpty(currentPlan)) {
        result = true;
      } else {
        result = false;
      }
    }
    return result;
  };

  useEffect(
    () => {
      fetchLatestBanner();
      checkCustomSubscriptions();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // here we will updates the results based on the times selected
  useEffect(() => {
    globalContext.setAlerts([]);
    if (init) fetchDashboardMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDates]);

  useEffect(() => {
    let options = [];
    if (filter === "weekly") {
      options = getWeeks(userData?.user?.created_at);
      setDateOptions(options);
      setFilterValue(options[0].value);
      setFilterDates({
        start: options[0].dates.start,
        end: options[0].dates.end,
      });
    } else {
      options = getMonths(userData?.user?.created_at);
      setDateOptions(options);
      setFilterValue(options[0].value);
      setFilterDates({
        start: options[0].dates.start,
        end: options[0].dates.end,
      });
    }
    setInit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <Layout title="Dashboard" routeName="dashboard">
      <div className="flex flex-col flex-1 w-full">
        <div className="flex flex-wrap -mx-px overflow-hidden">
          <div className="w-full px-px my-px overflow-hidden">
            <div className="flex flex-wrap 2md:flex-nowrap flex-auto -mx-2 overflow-hidden">
              <div className="w-full px-2 my-2 overflow-hidden">
                <WelcomeCard
                  currentPlan={currentPlan?.plan_name}
                  planStatus={currentPlan?.stripe_status}
                  planExpiry={
                    currentPlan?.trial_end || currentPlan?.current_period_end
                  }
                  isCustomPlan={custom.isCustom}
                  onClick={() => handleShowDetails(custom.data)}
                  loading={loadingPlan()}
                  admin={userData?.user?.role === "admin"}
                />
              </div>
              {banner && (
                <div className="w-full px-2 my-2 overflow-hidden">
                  <AlertBanner data={banner} />
                </div>
              )}
            </div>
          </div>
          {/* Performance Overview */}
          <Card
            padding="px-8 py-3"
            className="w-full border border-thBorder"
            height=""
          >
            <div className="w-full grid justify-center 7md:flex items-center 7md:justify-between">
              <div>
                <p className="text-2xl leading-9">Performance Overview</p>
              </div>
              <div className="w-1/2 2md:w-9/12">
                <div className="flex items-center space-x-5 flex-wrap justify-end">
                  <nav className="block 2md:bg-lightDarkGray 2md:rounded-full 2md:py-1 2md:px-1 relative">
                    {filters.map((obj, index) => {
                      return (
                        <FilterSelector
                          key={`filters-${index}`}
                          isActive={filter === obj.value}
                          onClick={() => setFilter(obj.value)}
                        >
                          {obj.label}
                        </FilterSelector>
                      );
                    })}
                  </nav>
                  <div className="w-72">
                    <Select
                      placeholder="Filter"
                      value={_.find(dateOptions, ["value", filterValue])?.label}
                      options={dateOptions}
                      onChange={({ value, dates }) => {
                        setFilterValue(value);
                        setFilterDates({
                          start: dates.start,
                          end: dates.end,
                        });
                      }}
                      readOnly={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
          {/* end: Performance Overview */}
          <div className="w-full px-px my-px overflow-hidden">
            <TotalStats
              start={filterDates.start}
              end={filterDates.end}
              dateLabel={_.find(dateOptions, ["value", filterValue])?.label}
            />
          </div>
          {/* Device Environment & Top Performing Popouts (Form Submissions) */}
          <Card
            padding=""
            className="w-full border border-thBorder mb-2"
            height=""
          >
            <div className="w-full grid 5md:grid-cols-2 divide-y 5md:divide-x 5md:divide-y-0 divide-thBorder">
              <div className="p-8">
                <TopInteractions
                  start={filterDates.start}
                  end={filterDates.end}
                />
              </div>
              <div className="p-8">
                <TopPopouts start={filterDates.start} end={filterDates.end} />
              </div>
            </div>
          </Card>
          {/* end: Device Environment & Top Performing Popouts (Form Submissions) */}
          <div className="w-full">
            <DailyStats start={filterDates.start} end={filterDates.end} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => {
  return {
    userData: state.user.userData,
  };
};

export default connect(mapStateToProps)(Dashboard);
